import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Lock, ExternalLink, KeyRound } from 'lucide-react'
import { trackUpgradeCtaClicked } from '@/shared/utils/analytics'
import { BUY_TEST_SERIES_URL } from '../utils/constants'

interface UnlockDialogProps {
  isOpen: boolean
  onClose: () => void
  /** e.g. "Unlock syllabus-wise PYQs" */
  title: string
  /** What the feature gives once unlocked */
  description?: string
  /** Analytics source for the CTA clicks */
  source: string
}

/** Shown to free users (no redeemed activation key) who try to open a paid feature */
export default function UnlockDialog({ isOpen, onClose, title, description, source }: UnlockDialogProps) {
  const navigate = useNavigate()

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="unlock-dialog-title"
        className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="space-y-2 text-center">
          <div className="flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50">
              <Lock className="h-7 w-7 text-blue-600" />
            </div>
          </div>
          <h2 id="unlock-dialog-title" className="text-lg font-bold text-gray-900">
            {title}
          </h2>
          <p className="text-sm text-gray-500">
            {description ? `${description} ` : ''}Buy any test series to unlock it.
          </p>
        </div>

        <div className="mt-5 space-y-2">
          <a
            href={BUY_TEST_SERIES_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackUpgradeCtaClicked(`${source}_buy`)}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 font-semibold text-white transition-colors hover:bg-blue-700"
          >
            Buy a Test Series
            <ExternalLink className="h-4 w-4" />
          </a>
          <button
            onClick={() => {
              trackUpgradeCtaClicked(`${source}_activate`)
              navigate('/test-series')
            }}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-blue-600 py-2.5 font-medium text-blue-600 transition-colors hover:bg-blue-50"
          >
            <KeyRound className="h-4 w-4" />
            I have an activation key
          </button>
          <button
            onClick={onClose}
            className="w-full rounded-xl py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  )
}
